import { useContext, useEffect, useState } from "react";
import { t } from "i18next";

import { LoansContext } from "../../../../../../context/LoansContext";

import BuyDownOptions from "./BuyDownOptions";

import {
  formatCurrency,
  clearStalePricingAndLeverageOverrides,
} from "../../../../../../utils/v2/common";
import { SizingTypeValue } from "../../../../../../types/enums";

export default function BuyDownOptionsWrapper(props: any) {
  const { handleSaveApplication } = props;

  const {
    buyDownOptions,
    currentSelectedInterestRate,
    setCurrentSelectedInterestRate,
    loanDetails,
    setQuickLoanSizingData,
    quickLoanSizingData,
    setDetailedLoanSizingData,
    detailedLoanSizingData,
    detailedLoanSizingDataRef,
    updateDetailedLoanSizingData,
    setNewDebouncedValue,
    setPendingAuditEdits,
    pricingAdjustmentOverrides,
    setPricingAdjustmentOverrides,
    leverageAdjustedMaxOverrides,
    setLeverageAdjustedMaxOverrides,
  } = useContext(LoansContext);

  const [formattedBuyDownOptionsState, setFormattedBuyDownOptionsState] =
    useState([]);

  const sortInterestRates = (data: Array<any>) => {
    return data?.sort((a, b) => {
      const rateA = parseFloat(a.interestRate.replace("%", ""));
      const rateB = parseFloat(b.interestRate.replace("%", ""));
      return rateA - rateB;
    });
  };

  const sortDiscountPointPercentage = (data: Array<any>) => {
    return data?.sort((a, b) => {
      const rateA = parseFloat(a.discountPointPercentage);
      const rateB = parseFloat(b.discountPointPercentage);
      return rateA - rateB;
    });
  };

  // A genuine Credit-Committee entry (see LoanEconomics.ts's
  // pricingOverrideFlags/getDisplayValue) always takes priority over
  // whatever buydown row is/was selected — Quick Loan Sizing has no such
  // field, so it's never manually overridden this way.
  const isManuallyOverriddenInterestRate =
    loanDetails?.sizingType !== SizingTypeValue.QUICK_LOAN_SIZING &&
    Boolean(detailedLoanSizingData?.loanEconomics?.pricingOverrideFlags?.interestRate);

  useEffect(() => {
    if (isManuallyOverriddenInterestRate) {
      // Don't touch loanEconomics.interestRate at all here — writing the
      // buydown default would immediately clobber the manual entry — and
      // clear the "selected" buydown row so the table doesn't keep showing
      // a stale/incorrect selection while a manual override is in effect.
      // The row list itself (formattedBuyDownOptionsState) still updates
      // normally below — CC can still pick a row to replace their entry.
      if (Object.keys(currentSelectedInterestRate ?? {}).length) {
        setCurrentSelectedInterestRate({});
      }
      const formattedBuyDownOptions: any = sortInterestRates(
        buyDownOptions?.map((option: any) => ({
          interestRate: `${(option?.interestRate * 100)?.toFixed(3)}%`,
          discountPoints: `${option?.discountPointPercentage?.toFixed(4)}`,
          discountPointsWithDollar: formatCurrency(
            option?.discountPointAmount,
            2,
          ),
        })) ?? [],
      );
      setFormattedBuyDownOptionsState(formattedBuyDownOptions);
      return;
    }
    // Rounded-string comparison, not raw float equality — buyDownOptions'
    // interestRate is a fraction computed by the rate-sheet evaluation via
    // a different arithmetic path than currentSelectedInterestRate's
    // string-parsed percentage, so strict === essentially never matches
    // (same reasoning as LoansPagev2's updateLeverageWithBuyDownOptions,
    // which already compares this same pair via .toFixed(3) strings). A
    // failed match here meant findCurrentSelection was always undefined,
    // so the "nothing selected yet" fallback below fired on every
    // buyDownOptions refresh, resetting the loan's interestRate to
    // sortedBuydownOptions[0] regardless of what was actually selected.
    const findCurrentSelection = buyDownOptions?.find(
      (option: any) =>
        (option.interestRate * 100)?.toFixed(3) ===
        parseFloat(
          currentSelectedInterestRate?.interestRate?.replace("%", "") ?? "",
        )?.toFixed(3)
    );
    const sortedBuydownOptions = sortDiscountPointPercentage(buyDownOptions);
    let formattedBuyDownOptions: any = [];
    sortedBuydownOptions?.forEach((option: any, index : number) => {
      if (index === 0) {
        // Whatever's already selected (a manual pick, or the rate a
        // previously-approved pricing exception was raised against) takes
        // priority — sortedBuydownOptions[0] is only a fallback for when
        // nothing is selected yet. This used to feed loanEconomics.
        // interestRate from `option` (sortedBuydownOptions[0]) UNCONDITIONALLY
        // below, outside this branch — since this effect re-runs on every
        // buyDownOptions refresh (i.e. after every guideline
        // re-evaluation), that silently overwrote the user's actual
        // selection with the first sorted option's rate on every cycle,
        // right after handleInterestChange had just set the real one.
        const selectedInterestRate = findCurrentSelection
          ? currentSelectedInterestRate?.interestRate
          : `${(option?.interestRate * 100)?.toFixed(3)}%`;
        if (!findCurrentSelection) {
          setCurrentSelectedInterestRate({
            interestRate: selectedInterestRate,
            discountPoints: `${option?.discountPointPercentage?.toFixed(4)}`,
            discountPointsWithDollar: formatCurrency(
              option?.discountPointAmount
            ),
            totalBuyYield: option?.totalBuyYield,
          });
        } else {
          setCurrentSelectedInterestRate({
            interestRate: currentSelectedInterestRate?.interestRate,
            discountPoints: currentSelectedInterestRate?.discountPoints,
            discountPointsWithDollar:
              currentSelectedInterestRate?.discountPointsWithDollar,
            totalBuyYield: currentSelectedInterestRate?.totalBuyYield,
          });
        }
        const interestRateForLoanEconomics = selectedInterestRate?.replace(
          "%",
          "",
        );
        // interestRate IS still written to loanEconomics here — getRequestBodyForGuidelines/
        // resolveLoanPricing (utils/v2/common.ts) reads this exact field to
        // price the guideline evaluation at the selected buydown rate, so
        // without it the guideline call gets no rate and returns no max loan
        // amount / buydown options at all. pricingOverrideFlags.interestRate
        // is explicitly kept false, though (never true here) — that's what
        // keeps this auto-computed value from showing up in the
        // Credit-Committee-only override input (see LoanEconomics.ts's
        // getDisplayValue), even though the underlying value is populated.
        if (loanDetails?.sizingType === SizingTypeValue.QUICK_LOAN_SIZING) {
          // Functional updater, not a spread of this closure's
          // quickLoanSizingData snapshot — this effect fires on every
          // buyDownOptions change (i.e. after every fetchGuidelines cycle,
          // triggered by any field edit), and a full-object spread here
          // would silently revert any edit made to another section in the
          // meantime. Same class of bug as detailedLoanSizingData below.
          setQuickLoanSizingData((prev: any) => ({
            ...prev,
            loanEconomics: {
              ...prev?.loanEconomics,
              interestRate: interestRateForLoanEconomics,
            },
          }));
        } else {
          updateDetailedLoanSizingData((prev: any) => ({
            ...prev,
            loanEconomics: {
              ...prev?.loanEconomics,
              interestRate: interestRateForLoanEconomics,
              pricingOverrideFlags: {
                ...prev?.loanEconomics?.pricingOverrideFlags,
                interestRate: false,
              },
            },
          }));
        }
      }
      formattedBuyDownOptions.push( {
        interestRate: `${(option?.interestRate * 100)?.toFixed(3)}%`,
        discountPoints: `${option?.discountPointPercentage?.toFixed(4)}`,
        discountPointsWithDollar: formatCurrency(
          option?.discountPointAmount,
          2
        ),
      });
    });
    formattedBuyDownOptions = sortInterestRates(formattedBuyDownOptions);
    setFormattedBuyDownOptionsState(formattedBuyDownOptions);
    // Narrowed to sizingType (the only loanDetails field this effect reads),
    // same fix as LoansPagev2's updateChips dependency array (877a70c42f) —
    // the full loanDetails object gets a new reference on every unrelated
    // update elsewhere on the loan (e.g. takeOutPartner assignment), which
    // was re-running this whole buyDownOptions/interestRate cascade for
    // edits that can't possibly change its result. Same reasoning for
    // isManuallyOverriddenInterestRate over the full detailedLoanSizingData.
  }, [buyDownOptions, loanDetails?.sizingType, isManuallyOverriddenInterestRate]);

  const tableHeader = [
    {
      label: t("buyDownInterestRate"),
      headerClassName: "no-border-header border-right",
    },
    {
      label: t("discountPoints"),
      headerClassName: "no-border-header border-right",
    },
    {
      label: t("discountPointsWithDollar"),
      headerClassName: "no-border-header",
    },
  ];

  const handleInterestChange = (interestValue: any) => {
    const latestData = detailedLoanSizingData || detailedLoanSizingDataRef.current
    const currentValue = buyDownOptions?.find(
        (option: any) =>
          option.interestRate ===
          Number(+interestValue.interestRate?.replace("%", "") / 100)
      );

      const updatedInterestRate = { 
        ...interestValue, 
        totalBuyYield: currentValue.totalBuyYield 
      };

    setCurrentSelectedInterestRate(updatedInterestRate);

    if (loanDetails?.sizingType === SizingTypeValue.QUICK_LOAN_SIZING) {
      setQuickLoanSizingData({
        ...quickLoanSizingData,
        loanEconomics: {
          ...quickLoanSizingData?.loanEconomics,
          interestRate: (currentValue?.interestRate * 100).toFixed(3),
        },
      });
    } else {
      // interestRate IS written here — getRequestBodyForGuidelines/
      // resolveLoanPricing needs it to price the guideline evaluation at the
      // selected rate, same reasoning as the auto-sync effect above.
      // finalToorakPrice and both pricingOverrideFlags are cleared in this
      // SAME atomic object (not a separate setDetailedLoanSizingData call)
      // because updateDetailedLoanSizingData below replaces the whole
      // object from this same `latestData` snapshot: a second call clearing
      // before or after would either get clobbered by this replace, or
      // immediately clobber the new rate this replace sets. Keeping
      // pricingOverrideFlags.interestRate false is what stops this
      // auto-selected rate from showing up in the Credit-Committee-only
      // override input (see LoanEconomics.ts's getDisplayValue).
      const updatedLoanData = {
        ...latestData,                    // ← has latest borrowerInformation
        loanEconomics: {
          ...latestData?.loanEconomics,
          interestRate: (currentValue?.interestRate * 100).toFixed(3),
          finalToorakPrice: "",
          pricingOverrideFlags: {
            interestRate: false,
            finalToorakPrice: false,
          },
        },
      };

      updateDetailedLoanSizingData(updatedLoanData);

      // Only the audit-recording half of the CC interestRate/finalToorakPrice
      // clear runs here (see applyPricingOverrideFieldsWrite — the actual
      // write already happened atomically above). The Pricing card's
      // pricingAdjustmentOverrides and the Leverages card's "Adjusted Max"
      // leverageAdjustedMaxOverrides are deliberately NOT cleared by a
      // buydown selection (clearAdjustmentOverrides: false) — a rate pick
      // doesn't invalidate those overrides the way editing some unrelated
      // loan field does, and clearing them here was wiping out data the user
      // had just entered, right before it could ever reach the LeverageTable
      // UI or the rules request body (applyPricingAdjustmentOverrides/
      // computeLeverageAdjustments in utils/v2/common.ts).
      clearStalePricingAndLeverageOverrides({
        detailedLoanSizingData: latestData,
        loanDetails,
        setDetailedLoanSizingData,
        setPendingAuditEdits,
        pricingAdjustmentOverrides,
        setPricingAdjustmentOverrides,
        leverageAdjustedMaxOverrides,
        setLeverageAdjustedMaxOverrides,
        applyPricingOverrideFieldsWrite: false,
        clearAdjustmentOverrides: false,
      });
    }
    handleSaveApplication(
      Boolean(loanDetails?.tcLoanId),
      "",
      "",
      updatedInterestRate
    );
    // Forces a fresh guideline/partner evaluation — LoansPagev2's
    // fetchWithDebounce effect only re-fires on newDebouncedValue (or
    // sizingType/waiverDecisionVersion) changing, none of which
    // handleSaveApplication's own state updates touch. Without this, the
    // guideline API is never called again with this selection, so the
    // buy-down's rate never actually reaches loanPricing.actualInterestRate.
    setNewDebouncedValue?.(updatedInterestRate?.interestRate);
  };

  const finalToorakPrice = loanDetails?.loanEconomics?.finalToorakPrice;
  const creditCommitteeDiscountPoints =
    finalToorakPrice !== undefined && finalToorakPrice !== null && finalToorakPrice !== ""
      ? (100 - Number(finalToorakPrice)).toFixed(4)
      : null;

  return (
    <div className="w-100" style={{ marginTop: "10px" }}>
      <BuyDownOptions
        header={t("buyDownOptions")}
        tableHeader={tableHeader}
        tableBody={formattedBuyDownOptionsState}
        handleInterestChange={handleInterestChange}
        currentSelectedInterestRate={currentSelectedInterestRate}
        creditCommitteeDiscountPoints={creditCommitteeDiscountPoints}
      />
    </div>
  );
}
